/** Radio tab controls with shared keyboard navigation and selection indicators. */
"use client";

import { RadioGroup as ArkRadioGroup, useRadioGroup } from "@ark-ui/react/radio-group";
import { focusWithinRing, insetFocusRing } from "@decibeltrade/web-core/recipes";
import { ReactNode, useRef, useState } from "react";
import { css, cx, sva } from "styled-system/css";

import { FormControlProps } from "./form/form-field";
import { Tooltip } from "./tooltip";

type RadioTabGroupVariant = "textOnly" | "layoutSelector" | "rounded" | "segmented";
export interface RadioTabGroupItem<TValue extends string> {
  /** The value to identify the radio option */
  value: TValue;
  /** The label of the radio option */
  label: ReactNode;
  /** The icon component inside the radio option */
  Icon?: React.ComponentType<{ className?: string }>;
  /** If true, only the icon will be displayed with the label as an aria-label. */
  iconOnly?: boolean;
  /** If true, this individual radio option will be disabled. */
  disabled?: boolean;
  /** The tooltip content to display when hovering over the radio option */
  tooltip?: ReactNode;
}

export interface RadioTabGroupProps<TValue extends string> extends Partial<FormControlProps> {
  /** A ref to the underlying `RadioGroup.Root` element */
  ref?: React.RefObject<HTMLDivElement | null>;
  /** The options of the radio tab group. */
  options: Array<RadioTabGroupItem<TValue>>;
  /** The currently selected option */
  value?: TValue;
  /** The callback that runs each time an option is selected */
  onChange?: (value: TValue, changedByKeyboard?: boolean) => void;
  /** If true, all options in the radio group will be disabled. */
  disabled?: boolean;
  /** If true, the selected option will display a loading indicator. */
  loading?: boolean;
  /** The variant of the radio tab group. */
  variant?: RadioTabGroupVariant;
  /** The name attribute of the underlying input elements. Useful for form submission. */
  name?: string;
  /** Class name for applying additional styles to the radio group's wrapper element */
  className?: string;
  /** Accessible label for the radio group. Prefer `aria-labelledby` when a visible label exists. */
  "aria-label"?: string;
  /** ID of a visible element that labels the radio group. */
  "aria-labelledby"?: string;
}

/** A component that allows a single selection within a set of tabs. */
export function RadioTabGroup<TValue extends string>({
  ref,
  options,
  value,
  onChange,
  disabled,
  loading,
  variant,
  name,
  className,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: RadioTabGroupProps<TValue>) {
  const classes = radioTabGroupStyles({ variant, loading });

  const radioGroupState = useRadioGroup({
    value,
    onValueChange: (e) => {
      const newValue = e.value;
      if (newValue != null) {
        requestAnimationFrame(() => {
          const viaKeyboard = radioGroupState.getItemState({ value: newValue }).focusVisible;
          onChange?.(newValue as TValue, viaKeyboard);
        });
      }
    },
    disabled,
    orientation: "horizontal",
    name,
  });

  return (
    <ArkRadioGroup.RootProvider
      ref={ref}
      id={id}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      value={radioGroupState}
      className={cx(classes.root, className)}
    >
      <ArkRadioGroup.Indicator className={classes.indicator} />
      <div className={classes.itemsWrapper}>
        {options.map((option, i) => (
          <RadioItem key={i} option={option} variant={variant} />
        ))}
      </div>
    </ArkRadioGroup.RootProvider>
  );
}

interface RadioItemProps<TValue extends string> {
  option: RadioTabGroupItem<TValue>;
  variant: RadioTabGroupVariant | undefined;
}

function RadioItem<TValue extends string>({ option, variant }: RadioItemProps<TValue>) {
  const wasClicked = useRef(false);
  const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
  const classes = radioTabGroupStyles({ variant });

  const asChild = typeof option.label === "string" ? false : true;

  let focusRingStyles = insetFocusRing({ rounded: "200" });
  if (variant === "textOnly") {
    focusRingStyles = focusWithinRing({ rounded: "200" });
  }

  const labelProps = {
    className: cx("group", classes.itemLabel, focusRingStyles),
    "aria-label": option.iconOnly && typeof option.label === "string" ? option.label : undefined,
    "data-focus-visible": isKeyboardFocus || undefined,
    onMouseDown: () => {
      wasClicked.current = true;
      setIsKeyboardFocus(false);
    },
    onFocus: () => {
      if (!wasClicked.current) {
        // Is `wasClicked.current` is not true, we can assume keyboard navigation was used
        setIsKeyboardFocus(true);
      }
      wasClicked.current = false;
    },
    onBlur: () => {
      setIsKeyboardFocus(false);
    },
  };

  const itemLabelContent = asChild ? (
    <ArkRadioGroup.ItemControl className={css({ w: "full" })}>
      {option.label}
    </ArkRadioGroup.ItemControl>
  ) : (
    option.label
  );

  const itemContent = (
    <>
      {option.Icon && <option.Icon className={classes.itemIcon} />}
      {!option.iconOnly && (
        <ArkRadioGroup.ItemText className={classes.itemText}>
          {itemLabelContent}
        </ArkRadioGroup.ItemText>
      )}
      <ArkRadioGroup.ItemHiddenInput />
    </>
  );

  const item = asChild ? (
    <ArkRadioGroup.Item value={option.value} disabled={option.disabled} asChild>
      <label {...labelProps}>{itemContent}</label>
    </ArkRadioGroup.Item>
  ) : (
    <ArkRadioGroup.Item value={option.value} disabled={option.disabled} {...labelProps}>
      {itemContent}
    </ArkRadioGroup.Item>
  );

  const wrappedItem = <div className={classes.itemWrapper}>{item}</div>;

  return option.tooltip ? (
    <Tooltip content={option.tooltip} placement="bottom">
      {wrappedItem}
    </Tooltip>
  ) : (
    wrappedItem
  );
}

const radioTabGroupStyles = sva({
  slots: ["root", "indicator", "itemsWrapper", "itemWrapper", "itemLabel", "itemText", "itemIcon"],
  base: {
    root: {
      display: "flex",
      gap: "16",
      flexDirection: "column",
    },
    indicator: {
      bg: "radio.background.checked",
      rounded: "200",
      h: "[var(--height)]",
      w: "[var(--width)]",
      top: "[var(--top)]",
      left: "[var(--left)]",
    },
    itemsWrapper: {
      display: "flex",
      gap: "4",
      borderStyle: "solid",
      borderWidth: "100",
      borderColor: "border.secondary",
      rounded: "300",
      p: "2",
    },
    itemWrapper: {
      display: "flex",
      flex: "1",
    },
    itemLabel: {
      display: "flex",
      flex: "1",
      gap: "2",
      alignItems: "center",
      justifyContent: "center",
      px: "8",
      py: "4",
      cursor: { base: "pointer", _disabled: "not-allowed" },
      "& > svg": {
        transform: "rotate(0deg)",
        transitionPreset: "transform",
      },
      "&[aria-expanded=true] > svg": {
        transform: "rotate(90deg)",
        transitionPreset: "transform",
      },
      _disabled: {
        cursor: "not-allowed",
      },
    },
    itemText: {
      display: "flex",
      position: "relative",
      textStyle: "body.md",
      textAlign: "center",
      color: {
        base: "text.secondary",
        _groupHover: "text.primary",
        _groupChecked: "text.secondary!",
        _disabled: "text.tertiary!",
      },
      transitionPreset: "colors",
    },
    itemIcon: {
      h: "14",
      w: "14",
      flexShrink: 0,
      color: {
        base: "icon.secondary",
        _groupHover: "icon.primary",
        _groupChecked: "icon.secondary!",
        _disabled: "icon.tertiary!",
      },
      transitionPreset: "colors",
    },
  },
  variants: {
    loading: {
      true: {
        indicator: {
          position: "relative",
          overflow: "hidden",
          _after: {
            content: "''",
            position: "absolute",
            inset: "0",
            backgroundImage:
              "linear-gradient(90deg, token(colors.skeleton.shimmer1) 0%, token(colors.skeleton.shimmer2) 20%, token(colors.skeleton.shimmer3) 60%, token(colors.skeleton.shimmer1))",
            animationStyle: "shimmer",
          },
        },
      },
    },
    variant: {
      textOnly: {
        itemsWrapper: {
          gap: "0",
          justifyContent: "space-between",
          p: "0",
          w: "full",
          borderWidth: "[0]",
        },
        itemWrapper: {
          flex: "[0]",
        },
        itemLabel: {
          px: "0",
          py: "0",
          rounded: "0",
          w: "max",
        },
        itemText: {
          color: {
            base: "text.tertiary",
            _groupHover: "text.secondary",
          },
        },
        itemIcon: {
          h: "16",
          w: "16",
          color: {
            base: "icon.tertiary",
          },
          transitionPreset: "colors",
        },
      },
      layoutSelector: {
        itemsWrapper: {
          rounded: "0",
          p: "0",
        },
        itemLabel: {
          px: "0",
          py: "0",
          cursor: "pointer",
          rounded: "300",
          borderWidth: "100",
          borderColor: "border.secondary",
          _hover: {
            borderColor: "border.hover",
          },
          "&[data-state=checked]": {
            borderColor: "border.active",
            _hover: {
              borderColor: "border.active",
            },
          },
        },
        itemText: {
          flex: "1",
        },
      },
      rounded: {
        indicator: {
          rounded: "full",
          bg: "background.secondary",
        },
        itemsWrapper: {
          rounded: "full",
          p: "0",
          borderColor: "border.tertiary",
        },
        itemLabel: {
          py: "[9px]",
        },
        itemText: {
          textStyle: "body.lg",
        },
        itemIcon: {
          h: "24",
          w: "24",
        },
      },
      segmented: {
        indicator: {
          bg: "background.tertiary",
          rounded: "300",
        },
        itemsWrapper: {
          bg: "background.secondary",
          rounded: "300",
          borderWidth: "[0]",
          p: "0",
          gap: "0",
          w: "full",
        },
        itemLabel: {
          px: "12",
          py: "8",
        },
        itemText: {
          textStyle: "highlight.lg",
          color: {
            base: "text.tertiary",
            _groupHover: "text.secondary",
            _groupChecked: "text.secondary!",
          },
        },
      },
    },
  },
  compoundVariants: [
    {
      variant: ["textOnly", "layoutSelector"],
      css: {
        indicator: {
          bg: "transparent",
          w: "0!",
          left: "0!",
        },
        itemsWrapper: {
          border: "none",
          borderColor: "transparent",
          rounded: "0",
          p: "0",
          position: "relative",
        },
        itemLabel: {
          px: "0",
          py: "0",
        },
        itemText: {
          flex: "1",
        },
      },
    },
  ],
});
